import { useEffect } from "react";
import { X, MapPin, Calendar, Sparkles, User, Compass } from "lucide-react";
import BoardingPass from "@/components/BoardingPass";
import { TID } from "@/constants/testIds";

/**
 * Slide-over panel showing every field of a single event registration.
 *
 * When the registration is `completed`, the embedded BoardingPass component
 * exposes the Download PNG / Share on WhatsApp / Copy link actions the admin
 * needs. For partial (`step_1_only`) registrations, we hide the ticket block.
 */
export default function RegistrationDetail({ registration, onClose }) {
  useEffect(() => {
    if (!registration) return () => {};
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [registration, onClose]);

  if (!registration) return null;

  const p = registration.payload || {};
  const ls = registration.lead_score || null;
  const isPartial = (registration.stage || "completed") === "step_1_only";
  const band = ls?.band;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid={TID.regDetailOverlay}>
      <button
        aria-label="Close details"
        onClick={onClose}
        className="flex-1 bg-void/50 backdrop-blur-sm"
      />
      <aside
        data-testid={TID.regDetailPanel}
        className="w-full max-w-2xl bg-offwhite overflow-y-auto shadow-2xl slide-in-right"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-forest text-cream px-6 py-4 border-b border-lime/30 flex items-center justify-between">
          <div className="min-w-0">
            <div className="eyebrow text-lime">
              {isPartial ? "Step 1 only · Abandoned" : "Full registration"}
            </div>
            <div className="font-display text-xl md:text-2xl truncate">
              {p.name || "Unnamed"}
            </div>
            <div className="text-xs text-cream/70 mt-0.5">
              {p.mobile} · {p.email}
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {band && <BigBandChip band={band} score={ls?.total} />}
            <button
              onClick={onClose}
              data-testid={TID.regDetailClose}
              className="w-9 h-9 rounded-full grid place-items-center bg-cream/10 hover:bg-cream/20 text-cream transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Boarding pass — completed only */}
          {!isPartial && registration.ticket && (
            <Section icon={Sparkles} label="Entry pass">
              <div data-testid={TID.regDetailPass}>
                <BoardingPass registration={registration} />
              </div>
            </Section>
          )}

          {/* Identity & Profile */}
          <Section icon={User} label="Identity & profile">
            <Grid>
              <Item label="Name" value={p.name} />
              <Item label="Mobile" value={p.mobile} />
              <Item label="Email" value={p.email} full />
              <Item label="City" value={p.city} icon={<MapPin size={11} />} />
              <Item label="Age bracket" value={p.age_bracket} />
              <Item label="Occupation" value={p.occupation} full />
              <Item label="Company" value={p.company} />
              <Item label="Designation" value={p.designation} />
              <Item label="Function / Industry" value={p.business_function} full />
            </Grid>
          </Section>

          {/* Intent Signals */}
          {!isPartial && (
            <Section icon={Compass} label="Intent signals · Lead scoring inputs">
              <Grid>
                <Item label="Purpose of visit" value={p.purpose} full />
                <Item label="How long searching" value={p.search_duration} />
                <Item label="Financing" value={p.financing} />
                <Item label="Willing to visit site" value={p.visit_confirmation} />
                <Item label="Explored similar" value={p.explored_similar} />
                <Item label="Decision involvement" value={p.decision_involvement} />
                <Item label="What matters most" value={p.what_matters} />
                <Item label="Primary home user" value={p.home_user} />
                <Item label="Referred by" value={p.referred_by} />
                {p.sales_person_name && (
                  <Item label="Sales person's name" value={p.sales_person_name} />
                )}
              </Grid>
            </Section>
          )}

          {/* Lead score breakdown */}
          {!isPartial && ls && (
            <Section icon={Sparkles} label="Lead scoring · Server-computed">
              <div className="rounded-xl border border-forest/15 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="eyebrow text-forest/60">Total score</div>
                    <div className="font-display text-forest text-3xl leading-none mt-1">
                      {ls.total}{" "}
                      <span className="text-sm text-forest/50 font-body">
                        / {ls.max_possible ?? 14}
                      </span>
                    </div>
                  </div>
                  {band && <BigBandChip band={band} score={ls.total} />}
                </div>
                {ls.breakdown && (
                  <div className="mt-4 divide-y divide-forest/10">
                    {Object.entries(ls.breakdown).map(([key, val]) => (
                      <div key={key} className="flex items-center justify-between py-2">
                        <span className="text-forest/70 text-xs capitalize">
                          {key.replaceAll("_", " ")}
                        </span>
                        <span className="font-mono text-sm text-forest">+{val}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Prize */}
          {!isPartial && registration.prize && (
            <Section icon={Sparkles} label="Spin wheel prize">
              <div className="rounded-xl border border-lime/50 bg-forest text-cream p-4">
                <div className="eyebrow text-lime">Won</div>
                <div className="font-display text-xl mt-1">{registration.prize.label}</div>
                <div className="text-cream/70 text-sm mt-2">{registration.prize.description}</div>
                <div className="text-[11px] text-cream/50 mt-3">
                  Revealed at {fmtDT(registration.prize.revealed_at)}
                </div>
              </div>
            </Section>
          )}

          {/* Meta */}
          <Section icon={Calendar} label="Metadata">
            <Grid>
              <Item label="Registration ID" value={registration.id} mono full />
              <Item label="Stage" value={registration.stage || "completed"} />
              <Item label="Source" value={p.source} />
              <Item
                label="Attendance confirmed"
                value={p.attendance_confirmed === false ? "No" : "Yes"}
              />
              <Item
                label="Consent"
                value={p.consent === false ? "No" : "Yes"}
              />
              <Item label="Created" value={fmtDT(registration.created_at)} />
              {registration.updated_at && (
                <Item label="Updated" value={fmtDT(registration.updated_at)} />
              )}
            </Grid>
          </Section>
        </div>
      </aside>
    </div>
  );
}

function Section({ icon: Icon, label, children }) {
  return (
    <section>
      <div className="eyebrow text-teal mb-3 inline-flex items-center gap-2">
        {Icon && <Icon size={13} />} {label}
      </div>
      {children}
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

function Item({ label, value, icon, mono, full }) {
  const display = value === "" || value === null || value === undefined ? (
    <span className="text-forest/30">—</span>
  ) : (
    value
  );
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-[10px] text-forest/50 tracking-widest uppercase inline-flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className={`text-forest text-sm mt-1 break-words ${mono ? "font-mono text-xs" : ""}`}>
        {display}
      </div>
    </div>
  );
}

function BigBandChip({ band, score }) {
  const map = {
    Hot: "bg-lime text-void",
    Warm: "bg-teal text-cream",
    Cold: "bg-cream text-forest",
  };
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${map[band] || "bg-cream/20 text-cream"}`}
    >
      {band}
      {typeof score === "number" && (
        <span className="opacity-80 text-[11px]">· {score}/14</span>
      )}
    </span>
  );
}

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}
